<template>
  <section :class="$style.section">
    <div :class="$style.container">
      <h2 :class="$style.title">Наша культура</h2>

      <div :class="$style.gallery">
        <!-- Photos galley -->
        <stack
          :column-min-width="263"
          :gutter-width="15"
          :gutter-height="15"
          :monitor-images-loaded="true"
        >
          <!-- Gallery item -->
          <stack-item
            v-for="(image, index) in images"
            :class="$style.photo"
            :key="index"
            style="transition: transform 300ms"
          >
            <img
              :src="image"
              :class="$style.galleryImage"
              alt="Culture photo"
              decoding="async"
              @click="openPopup(index)"
            >
          </stack-item>
        </stack>
      </div>

      <!-- Section actions -->
      <div :class="$style.actions">
        <button
          v-show="!expanded"
          :class="$style.button"
          aria-label="Показать все фото"
          type="button"
          @click="toggleExpanded"
        >
          Показать все
        </button>
      </div>
    </div>

    <!-- Slider dialog -->
    <el-dialog
      :visible.sync="popupOpened"
      :class="['dialogWithCarousel', $style.dialog]"
      top="5vh"
    >
      <el-carousel
        ref="carousel"
        :interval="4000"
        :autoplay="false"
        :initial-index="initialIndex"
        indicator-position="none"
        arrow="always"
      >
        <el-carousel-item
          v-for="(photo, index) in fullsizePhotos"
          :key="index"
          :class="$style.sliderItem">
          <el-image
            :src="photo"
            :class="$style.sliderPhoto"
            alt="Culture photo in slider"
          >
            <BaseLoader
              slot="placeholder"
              :class="$style.loader" />
          </el-image>
        </el-carousel-item>
      </el-carousel>
    </el-dialog>
  </section>
</template>

<script>
import { Stack, StackItem } from 'vue-stack-grid';
import CulturePhotos from '../data/culture-photos';
import BaseLoader from './BaseLoader';

/**
 * Team culture component.
 */
export default {
  name: 'TeamCulture',
  components: {
    Stack,
    StackItem,
    BaseLoader,
  },
  data: () => ({
    /**
     * Gallery expanded state.
     */
    expanded: false,
    /**
     * Popup open state.
     */
    popupOpened: false,
    /**
     * Gallery images.
     */
    images: [],
    /**
     * Slider initial index state.
     */
    initialIndex: 0,
    previewPhotos: CulturePhotos.previews,
    fullsizePhotos: CulturePhotos.full,
  }),
  computed: {
    /**
     * First photos to render in gallery.
     *
     * @returns {Array<string>}
     */
    initialPhotos() {
      return this.previewPhotos.slice(0, 8);
    },
  },
  watch: {
    expanded() {
      if (this.expanded) {
        this.images = this.previewPhotos;
      } else {
        this.images = this.initialPhotos;
      }
    },
    popupOpened() {
      // Hack to remove hidden body overflow on close modal.
      if (!this.popupOpened) {
        $('body').css('overflow', '');
      }
    },
  },
  created() {
    this.images = this.initialPhotos;
  },
  methods: {
    /**
     * Toggle expanded gallery state.
     */
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    /**
     * Open photo details popup.
     *
     * @param {number} index - Image index.
     */
    openPopup(index) {
      this.popupOpened = true;
      this.initialIndex = index;
      this.setActiveSlide(index);
    },
    /**
     * Close photo details popup.
     */
    closePopup() {
      this.popupOpened = false;
    },
    /**
     * Set active slide of popup slider.
     *
     * @param {number} index - Image index.
     */
    setActiveSlide(index) {
      if (this.$refs.carousel) {
        this.$refs.carousel.setActiveItem(index);
      }
    },
  },
};
</script>

<style lang="scss" module>
@import '../styles/variables';
@import '../styles/helpers';

.section {
  @extend %section;

  @include media('<=desktop') {
    padding-bottom: 150px;
  }

  @include media('<=phone') {
    padding-bottom: 100px;
  }
}

.container {
  @extend %container;
  max-width: 1140px;

  @include media('<desktop-xlg') {
    max-width: 992px;
  }
}

.title {
  @extend %sectionTitle;
  margin: 0 0 relative-mb(80, 46);
  text-align: center;

  @include media('<=phone') {
    margin-bottom: 30px;
  }
}

.gallery {
  min-height: 300px;
}

.galleryImage {
  @extend %hoverBlock;
}

.photo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: center;
  overflow: hidden;
  margin: 0;
  object-fit: fill;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 4px;
    transition: all 0.4s ease-in-out;
  }
}

.loader {
  z-index: 4;
  width: 100%;
  height: 100%;
}

/*========== Actions styles ===========*/
.actions {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.button {
  @extend %button;
  width: 160px;
  padding-top: 20px;
  padding-bottom: 20px;

  @include media('<=phone') {
    display: block;
    margin: 0 auto;
    width: auto;
    padding-top: 15px;
    padding-bottom: 15px;
  }
}
/*======== End of Actions styles =======*/

/*========== Dialog ===========*/
.dialog {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;

  .sliderPhoto {
    position: relative;
    width: 100%;
    max-height: 85vh;
    height: 100%;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
  }

  .sliderItem {
    display: flex;
    align-items: center;
    justify-content: center;
    /* stylelint-disable */
    img {
      object-fit: contain;
    }
  }
}

.dialog [class='el-dialog'] {
  background-color: $transparent;
  box-shadow: none;
}

.dialog [class='el-dialog__body'] {
  padding: 0;
}

@media (max-width: 1366px) {
  .dialog [role='dialog'] {
    margin: auto 20px;
    width: 100%;
  }

  [class='el-dialog'] {
    width: 100%;
  }
}

@media (min-width: 769px) {
  .dialog {
    [class='el-carousel__container'] {
      height: 85vh;
    }
  }
}

@media (max-width: 768px) {
  [class='el-dialog'] {
    width: 60%;
  }

  .sliderPhoto {
    max-height: 85vh;
    height: 100%;
  }

  .dialog {
    [role='dialog'] {
      margin: auto 5px;
    }

    [class='el-carousel__container'] {
      height: 85vh;
    }
  }
}

@media (max-width: 415px) {
  .dialog [role='dialog'] {
    margin: auto 2px;
  }
}
/*========== End of Dialog ===========*/
</style>



// WEBPACK FOOTER //
// src/components/TeamCulture.vue